import { useState, useMemo } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useMemberStore } from "../../../stores/memberStore.js";

const STATUS_COLORS = { online: '#22c55e', idle: '#f59e0b', dnd: '#ef4444', offline: 'rgba(255,255,255,0.2)' };
const STATUS_LABELS = { online: 'Online', idle: 'Idle', dnd: 'Do Not Disturb', offline: 'Offline' };

export function MemberDirectoryModal() {
  const { isOpen, type, onClose } = useModalStore();
  const onOpen   = useModalStore(s => s.onOpen);
  const members  = useMemberStore(s => s.members);

  const [search,   setSearch]   = useState('');
  const [selRole,  setSelRole]  = useState('all');
  const [selStatus,setSelStatus]= useState('all');
  const [sort,     setSort]     = useState('name'); // name | role | status

  const filtered = useMemo(() => {
    let list = [...members];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m => (m.name || m.username || '').toLowerCase().includes(q) || (m.role || '').toLowerCase().includes(q));
    }
    if (selRole !== 'all') list = list.filter(m => (m.role || 'Member') === selRole);
    if (selStatus !== 'all') list = list.filter(m => (m.status || 'offline') === selStatus);
    list.sort((a, b) => {
      if (sort === 'name')   return (a.name || a.username || '').localeCompare(b.name || b.username || '');
      if (sort === 'role')   return (a.role || '').localeCompare(b.role || '');
      if (sort === 'status') {
        const order = { online: 0, idle: 1, dnd: 2, offline: 3 };
        return (order[a.status] ?? 3) - (order[b.status] ?? 3);
      }
      return 0;
    });
    return list;
  }, [members, search, selRole, selStatus, sort]);

  const online = members.filter(m => (m.status || 'offline') !== 'offline').length;
  const roles  = ['all', ...new Set(members.map(m => m.role || 'Member').filter(Boolean))];

  if (!isOpen || type !== 'memberDirectory') return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 620, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>👥 Member Directory</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{members.length} members · {online} online</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>

          {/* Search + filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members…"
              style={{ flex: '1 1 160px', padding: '7px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none' }} />
            <select value={selRole} onChange={e => setSelRole(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
              {roles.map(r => <option key={r} value={r}>{r === 'all' ? 'All Roles' : r}</option>)}
            </select>
            <select value={selStatus} onChange={e => setSelStatus(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="idle">Idle</option>
              <option value="dnd">DND</option>
              <option value="offline">Offline</option>
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
              <option value="name">Sort: Name</option>
              <option value="role">Sort: Role</option>
              <option value="status">Sort: Status</option>
            </select>
          </div>
        </div>

        {/* Member list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No members match</div>
          ) : (
            filtered.map(m => {
              const name   = m.name || m.username || 'Unknown';
              const role   = m.role || 'Member';
              const status = m.status || 'offline';
              return (
                <div key={m.id || name}
                  onClick={() => { onClose(); setTimeout(() => onOpen('userProfile', { member: m }), 100); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  {/* Avatar + status dot */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#fff' }}>
                      {m.avatar || name[0]?.toUpperCase()}
                    </div>
                    <div style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: STATUS_COLORS[status], border: '2px solid var(--bg-primary,#13141f)' }} title={STATUS_LABELS[status]} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                      {role}
                      {m.activity && <span style={{ marginLeft: 8, color: 'rgba(99,102,241,0.8)' }}>· {m.activity}</span>}
                    </div>
                  </div>

                  {/* Role badge */}
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {role}
                  </span>

                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>→</span>
                </div>
              );
            })
          )}
        </div>

        <div style={{ padding: '10px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', fontSize: 11, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>
          Showing {filtered.length} of {members.length} members
        </div>
      </div>
    </div>
  );
}
